/**
 * Base Container Component
 * 
 * @copyright NIIT Technologies Ltd. 2017-18
 */
// Angular
import {
    Component, Injectable, Inject, ReflectiveInjector, NgZone, ResolvedReflectiveProvider,
    ElementRef, OnInit, OnDestroy, AfterContentInit, AfterViewInit, OnChanges,
    ViewChildren, ContentChildren, QueryList, HostListener, ChangeDetectorRef,
    Input, SimpleChanges, forwardRef, ViewContainerRef, ComponentFactoryResolver, ComponentRef
} from '@angular/core';
import {
    Http, RequestOptions, ResponseOptions, BaseRequestOptions, BaseResponseOptions,
    ConnectionBackend, BrowserXhr, XHRBackend, XSRFStrategy, CookieXSRFStrategy
} from '@angular/http';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { Router } from '@angular/router';
// Core
import { Event, NotificationType, BaseResponse, ErrorMessage } from '../common/base.data';
import { BaseComponent } from './base.component';
import { ControlComponent } from './control.component';
import { HTTPService } from '../service/http.service';
import { RestService } from '../service/rest.service';
// NGC Inputs
import { NgcCheckBoxComponent } from '../../controls/checkbox/ngc-checkbox.component';
import { NgcComboBoxComponent } from '../../controls/combobox/ngc-combobox.component';
import { NgcDateTimeInputComponent } from '../../controls/datetimeinput/ngc-datetimeinput.component';
import { NgcDropDownListComponent } from '../../controls/dropdownlist/ngc-dropdownlist.component';
import { NgcInputComponent } from '../../controls/input/ngc-input.component';
import { NgcListBoxComponent } from '../../controls/listbox/ngc-listbox.component';
import { NgcMaskedInputComponent } from '../../controls/maskedinput/ngc-maskedinput.component';
import { NgcNumberInputComponent } from '../../controls/numberinput/ngc-numberinput.component';
import { NgcPasswordInputComponent } from '../../controls/passwordinput/ngc-passwordinput.component';
import { NgcTextAreaComponent } from '../../controls/textarea/ngc-textarea.component';
// NGC Other
import { NgcButtonComponent } from '../../controls/button/ngc-button.component';
import { NgcDataTableComponent } from '../../controls/datatable/ngc-datatable.component';
import { NgcGridComponent } from '../../controls/grid/ngc-grid.component';
import { NgcIconComponent } from '../../controls/icon/ngc-icon.component';
import { NgcLinkComponent } from '../../controls/link/ngc-link.component';
import { NgcLoaderComponent } from '../../controls/loader/ngc-loader.component';
import { NgcMenuComponent } from '../../controls/menu/ngc-menu.component';
import { NgcNotificationComponent } from '../../controls/notification/ngc-notification.component';
import { NgcPopoverComponent } from '../../controls/popover/ngc-popover.component';
import { NgcTabsComponent } from '../../controls/tabs/ngc-tabs.component';
//

/**
 * Base Container Component for All Container Components
 */
export class ContainerComponent extends BaseComponent {
    private functionId: string = null;
    //
    protected restService: RestService = null;
    //
    // Input View Childs
    
    private checkBoxList: QueryList<NgcCheckBoxComponent>;
    
    private comboBoxList: QueryList<NgcComboBoxComponent>;
    
    private dataTimeInputList: QueryList<NgcDateTimeInputComponent>;
    
    private dropDownListList: QueryList<NgcDropDownListComponent>;
    
    private inputList: QueryList<NgcInputComponent>;
    
    private listBoxList: QueryList<NgcListBoxComponent>;
    
    private maskedInputList: QueryList<NgcMaskedInputComponent>;
    
    private numberInputList: QueryList<NgcNumberInputComponent>;
    
    private passwordInputList: QueryList<NgcPasswordInputComponent>;
    
    private textAreaList: QueryList<NgcTextAreaComponent>;

    // Other View Childs
    
    private buttonList: QueryList<NgcButtonComponent>;
    
    private dataTableList: QueryList<NgcDataTableComponent>;
    
    private gridList: QueryList<NgcGridComponent>;
    
    private iconList: QueryList<NgcIconComponent>;
    
    private iconLinkList: QueryList<NgcLinkComponent>;
    
    private loaderList: QueryList<NgcLoaderComponent>;
    
    private menuList: QueryList<NgcMenuComponent>;
    
    private notificationList: QueryList<NgcNotificationComponent>;
    
    private popOverList: QueryList<NgcPopoverComponent>;
    
    private tabsList: QueryList<NgcTabsComponent>;

    /**
     * Initialize
     */
    constructor(functionId: string,
        protected containerZone: NgZone, protected containerElement: ElementRef,
        protected containerParentElement: ViewContainerRef,
        protected containerComponentResolver: ComponentFactoryResolver) {
        //
        super(containerZone, containerElement, null);
        // Update Function Id for Propagation
        this.functionId = functionId;
        // Inject Rest Service
        try {
            // Resolve Rest Service
            const injector = ReflectiveInjector.resolveAndCreate([
                Http, BrowserXhr,
                { provide: RequestOptions, useClass: BaseRequestOptions },
                { provide: ResponseOptions, useClass: BaseResponseOptions },
                { provide: ConnectionBackend, useClass: XHRBackend },
                { provide: XSRFStrategy, useFactory: () => new CookieXSRFStrategy() },
            ]);
            const httpInjector = injector.resolveAndCreateChild([HTTPService]);
            const restInjector = httpInjector.resolveAndCreateChild([RestService]);
            // Inject Rest Service to Container
            this.restService = restInjector.get(RestService);
        } catch (e) {
            console.error("Failed to Inject Rest Service to Container");
        }
    }

    /**
     * On Initialization
     */
    public ngOnInit() {
        super.ngOnInit();
        // Subscribe Component Events
        this.subscribeComponentEvents();
    }

    /**
     * After View Init
     */
    public ngAfterViewInit() {
        super.ngAfterViewInit();
        // Propagate Data to Child
        this.propagate();
    }

    /**
     * Append Child to Final Control List
     * 
     * @param controlList Final Control List
     * @param childList Child Control List
     */
    private appendControlToList(controlList: Array<ControlComponent>, childList: QueryList<ControlComponent>): void {
        if (!controlList || !childList) {
            return;
        }
        // Copy to Final Control List
        childList.forEach(function (item, index, array) {
            controlList.push(item);
        });
    }

    /**
     * Append Child to Final Control Map
     * 
     * @param controlMap Final Control Map
     * @param childList Child Control List
     */
    private appendControlToMap(controlMap: Object, childList: QueryList<ControlComponent>): void {
        if (!controlMap || !childList) {
            return;
        }
        // Copy to Final Control List
        childList.forEach(function (item, index, array) {
            let formControlName = item["controlElement"].nativeElement.getAttribute("formcontrolname");
            //
            if (item && formControlName) {
                controlMap[formControlName] = item;
            }
        });
    }

    /**
     * Get Known View Childs
     */
    protected getKnownViewChilds(): Array<ControlComponent> {
        let controlList = new Array<ControlComponent>();
        // Create Full Control List
        // Input
        this.appendControlToList(controlList, this.checkBoxList);
        this.appendControlToList(controlList, this.comboBoxList);
        this.appendControlToList(controlList, this.dataTimeInputList);
        this.appendControlToList(controlList, this.dropDownListList);
        this.appendControlToList(controlList, this.inputList);
        this.appendControlToList(controlList, this.listBoxList);
        this.appendControlToList(controlList, this.maskedInputList);
        this.appendControlToList(controlList, this.numberInputList);
        this.appendControlToList(controlList, this.passwordInputList);
        this.appendControlToList(controlList, this.textAreaList);
        // Other
        this.appendControlToList(controlList, this.buttonList);
        this.appendControlToList(controlList, this.dataTableList);
        this.appendControlToList(controlList, this.gridList);
        this.appendControlToList(controlList, this.iconList);
        this.appendControlToList(controlList, this.iconLinkList);
        this.appendControlToList(controlList, this.loaderList);
        this.appendControlToList(controlList, this.menuList);
        this.appendControlToList(controlList, this.notificationList);
        this.appendControlToList(controlList, this.popOverList);
        this.appendControlToList(controlList, this.tabsList);
        //
        return controlList;
    }

    /**
     * Get Input Childs
     */
    protected getInputChildMap(): Object {
        let controlMap = {};
        // Create Full Control Map (Input)
        this.appendControlToMap(controlMap, this.checkBoxList);
        this.appendControlToMap(controlMap, this.comboBoxList);
        this.appendControlToMap(controlMap, this.dataTimeInputList);
        this.appendControlToMap(controlMap, this.dropDownListList);
        this.appendControlToMap(controlMap, this.inputList);
        this.appendControlToMap(controlMap, this.listBoxList);
        this.appendControlToMap(controlMap, this.maskedInputList);
        this.appendControlToMap(controlMap, this.numberInputList);
        this.appendControlToMap(controlMap, this.passwordInputList);
        this.appendControlToMap(controlMap, this.textAreaList);
        //
        return controlMap;
    }

    /**
     * Propagate
     */
    protected propagate(): void {
        const self = this;
        const childrens: Array<ControlComponent> = this.getKnownViewChilds();
        // Propagate Data
        if (childrens) {
            childrens.forEach(function (control: ControlComponent, index: number, array) {
                if (control) {
                    // Inject Function Id of Childs
                    control.functionId = self.functionId;
                }
            });
        }
    }

    /**
     * Propagate Error Messages
     * 
     * @param errorList Error List
     */
    protected propagateErrorMessages(errorList: Array<ErrorMessage>): void {
        let controlMap = this.getInputChildMap();
        // Inject Error Message
        if (errorList) {
            errorList.forEach(function (error: ErrorMessage, index: number, array) {
                if (error && error.referenceId) {
                    let control = controlMap[error.referenceId];
                    // Inject Control Error Message
                    if (control) {
                        (<ControlComponent>control).error = error.errorMessage;
                    }
                }
            });
        }
    }

    /**
     * Create Component Dynamically
     * 
     * @param component Component Type
     * @param model Component Model
     */
    protected createComponent(component: any, model: any): ComponentRef<any> {
        if (!component) {
            return null;
        }
        const factory = this.containerComponentResolver.resolveComponentFactory(component);
        const componentRef: ComponentRef<any> = this.containerParentElement.createComponent(factory);
        // Set Model
        if (model) {
            (<any>componentRef).instance.model = model;
        }
        //
        return componentRef;
    }

static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'checkBoxList': [{ type: ViewChildren, args: [NgcCheckBoxComponent, ] },],
'comboBoxList': [{ type: ViewChildren, args: [NgcComboBoxComponent, ] },],
'dataTimeInputList': [{ type: ViewChildren, args: [NgcDateTimeInputComponent, ] },],
'dropDownListList': [{ type: ViewChildren, args: [NgcDropDownListComponent, ] },],
'inputList': [{ type: ViewChildren, args: [NgcInputComponent, ] },],
'listBoxList': [{ type: ViewChildren, args: [NgcListBoxComponent, ] },],
'maskedInputList': [{ type: ViewChildren, args: [NgcMaskedInputComponent, ] },],
'numberInputList': [{ type: ViewChildren, args: [NgcNumberInputComponent, ] },],
'passwordInputList': [{ type: ViewChildren, args: [NgcPasswordInputComponent, ] },],
'textAreaList': [{ type: ViewChildren, args: [NgcTextAreaComponent, ] },],
'buttonList': [{ type: ViewChildren, args: [NgcButtonComponent, ] },],
'dataTableList': [{ type: ViewChildren, args: [NgcDataTableComponent, ] },],
'gridList': [{ type: ViewChildren, args: [NgcGridComponent, ] },],
'iconList': [{ type: ViewChildren, args: [NgcIconComponent, ] },],
'iconLinkList': [{ type: ViewChildren, args: [NgcLinkComponent, ] },],
'loaderList': [{ type: ViewChildren, args: [NgcLoaderComponent, ] },],
'menuList': [{ type: ViewChildren, args: [NgcMenuComponent, ] },],
'notificationList': [{ type: ViewChildren, args: [NgcNotificationComponent, ] },],
'popOverList': [{ type: ViewChildren, args: [NgcPopoverComponent, ] },],
'tabsList': [{ type: ViewChildren, args: [NgcTabsComponent, ] },],
};
}
interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
